import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Fingerprint, ShieldAlert } from "lucide-react";
import { isInstalledPwa, webauthnSupported, getEnrollStatus, enrollDevice, unlockDevice } from "@/lib/webauthn";
import { cn } from "@/lib/utils";

export default function AppUnlockGate({ children }) {
  const [gated] = useState(() => isInstalledPwa());
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [needsEnroll, setNeedsEnroll] = useState(false);
  const [error, setError] = useState("");
  const runningRef = useRef(false);

  // WebAuthn must be invoked from a real user gesture (button tap). Auto-invoking
  // navigator.credentials.get() on mount is blocked by Android/Chrome and leaves the
  // promise pending forever ("Verifying…" hang), so this runs ONLY from the button.
  const run = useCallback(async () => {
    if (runningRef.current) return; // re-entry guard against double taps
    runningRef.current = true;
    setBusy(true); setError("");
    try {
      try {
        await unlockDevice();
        setUnlocked(true);
      } catch (e) {
        if (e?.response?.status === 404) {
          // No passkey enrolled on this device/site yet → enrol within this same gesture.
          // The enrolment create() ceremony already verifies the user, so treat it as an
          // unlock (a second chained get() after the await would lose user activation).
          setNeedsEnroll(true);
          await enrollDevice();
          setNeedsEnroll(false);
          setUnlocked(true);
        } else {
          throw e;
        }
      }
    } catch (e) {
      setNeedsEnroll(false);
      setError(e?.response?.data?.detail || e?.message || "Unlock failed. Please try again.");
    } finally { setBusy(false); runningRef.current = false; }
  }, []);

  // Re-lock whenever the app returns to the foreground; the user re-unlocks with a tap.
  useEffect(() => {
    if (!gated || !webauthnSupported()) return;
    const onVis = () => { if (document.visibilityState === "visible") setUnlocked(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [gated]); // eslint-disable-line

  // Keep children mounted at all times; when locked, cover them with an opaque
  // overlay instead of unmounting. This preserves in-progress form state (e.g. a
  // half-filled "New walk-in") across app-switch re-locks, while hiding content.
  const showLock = gated && webauthnSupported() && !unlocked;

  return (
    <>
      {(!gated || !webauthnSupported()) ? children : <div aria-hidden={showLock} className={cn(showLock && "pointer-events-none select-none")}>{children}</div>}
      {showLock && (
        <main className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0F1729] p-6" data-testid="app-unlock-gate">
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-xl">
            <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-[#F5A623]/15 text-[#F5A623]">
              {error ? <ShieldAlert className="h-8 w-8" /> : <Fingerprint className="h-8 w-8" />}
            </div>
            <h1 className="font-display text-xl font-bold text-white">
              {needsEnroll ? "Set up device lock" : "Unlock TilesMate"}
            </h1>
            <p className="mt-2 text-sm text-white/60">
              {needsEnroll
                ? "For security, register this phone's Face ID / fingerprint. You'll use it to open the app."
                : "Verify with Face ID, fingerprint or your device PIN to continue. Your unsaved work is kept safe."}
            </p>
            {error && <p className="mt-3 rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-300" data-testid="unlock-error">{error}</p>}
            {error && <p className="mt-2 text-[11px] text-white/40">If this keeps happening, ask an admin to reset device lock for your account in Settings.</p>}
            <Button onClick={run} disabled={busy} data-testid="unlock-btn"
              className="mt-6 w-full gap-2 bg-[#F5A623] text-[#23345A] hover:bg-[#F5A623]/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}
              {busy ? "Verifying…" : needsEnroll ? "Enrol this device" : "Unlock"}
            </Button>
          </div>
        </main>
      )}
    </>
  );
}
